import { useState, useEffect } from 'react';
import { pointsService } from '../services/pointsService';
import { useAuth } from '../contexts/AuthContext';
import { formatters } from '../utils/formatters';

export const usePoints = () => {
  const [pointsHistory, setPointsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.companyId) {
      loadPointsHistory();
    } else if (user?.role === 'admin') {
      loadAllPointsHistory();
    } else {
      setPointsHistory([]);
      setLoading(false);
    }
  }, [user]);

  const loadPointsHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user?.companyId) {
        setPointsHistory([]);
        return;
      }
      
      const data = await pointsService.getByCompany(user.companyId);
      setPointsHistory(data);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar histórico de pontos:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllPointsHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pointsService.getAll();
      setPointsHistory(data);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar todo o histórico de pontos:', err);
    } finally {
      setLoading(false);
    }
  };

  const addPoints = async (customerId, points, description) => {
    try {
      // Validar dados de entrada
      if (!customerId || !customerId.trim()) {
        throw new Error('ID do cliente é obrigatório');
      }
      
      if (!points || isNaN(points) || points <= 0) {
        throw new Error('Quantidade de pontos deve ser um número positivo');
      }
      
      if (!user.companyId) {
        throw new Error('ID da empresa não encontrado no usuário');
      }
      
      const pointsData = {
        customerId: customerId.trim(),
        companyId: user.companyId.trim(),
        points: parseInt(points),
        description: description ? description.trim() : 'Pontos adicionados',
        type: 'add',
        date: formatters.getCurrentTimestamp() // Timestamp atual
      };
      
      const newPointsEntry = await pointsService.addPoints(pointsData);
      
      setPointsHistory(prev => [newPointsEntry, ...prev]);
      return newPointsEntry;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao adicionar pontos:', err);
      throw err;
    }
  };

  const redeemPoints = async (customerId, points, description) => {
    try {
      const newPointsEntry = await pointsService.redeemPoints({
        customerId,
        points,
        description,
        type: 'redeem',
        date: formatters.getCurrentTimestamp(), // Timestamp atual
        companyId: user.companyId
      });
      
      setPointsHistory(prev => [newPointsEntry, ...prev]);
      return newPointsEntry;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao resgatar pontos:', err);
      throw err;
    }
  };

  const getCustomerHistory = (customerId) => {
    return pointsHistory.filter(entry => entry.customerId === customerId);
  };

  const getCompanyTotalPoints = (companyId) => {
    return pointsHistory
      .filter(entry => entry.companyId === companyId && entry.type === 'add')
      .reduce((total, entry) => total + entry.points, 0);
  };

  const getPointsStats = () => {
    const totalPointsAdded = pointsHistory
      .filter(entry => entry.type === 'add')
      .reduce((total, entry) => total + entry.points, 0);
    
    const totalPointsRedeemed = pointsHistory
      .filter(entry => entry.type === 'redeem')
      .reduce((total, entry) => total + entry.points, 0);
    
    const totalTransactions = pointsHistory.length;
    
    const recentTransactions = pointsHistory
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);

    return {
      totalPointsAdded,
      totalPointsRedeemed,
      netPoints: totalPointsAdded - totalPointsRedeemed,
      totalTransactions,
      recentTransactions
    };
  };

  const getMonthlyStats = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const monthlyHistory = pointsHistory.filter(entry => {
      const entryDate = new Date(entry.date);
      return entryDate.getMonth() === currentMonth && entryDate.getFullYear() === currentYear;
    });
    
    const monthlyAdded = monthlyHistory
      .filter(entry => entry.type === 'add')
      .reduce((total, entry) => total + entry.points, 0);
    
    const monthlyRedeemed = monthlyHistory
      .filter(entry => entry.type === 'redeem')
      .reduce((total, entry) => total + entry.points, 0);
    
    return {
      monthlyAdded,
      monthlyRedeemed,
      monthlyTransactions: monthlyHistory.length
    };
  };

  return {
    pointsHistory,
    loading,
    error,
    addPoints,
    redeemPoints,
    getCustomerHistory,
    getCompanyTotalPoints,
    getPointsStats,
    getMonthlyStats,
    reload: loadPointsHistory,
    reloadAll: loadAllPointsHistory
  };
};